import { useCallback, useEffect, useState } from "react";

import MODALS from "../constants/modals";
import ROLES from "../constants/roles";
import { IScheduleModalProps } from "../types/modal";

import { useAppDispatch } from "../hooks/redux";
import { closeModalById, openModal } from "../features/modals";

import axios from "axios";
import moment from "moment";

import {
  Dialog,
  DialogActions,
  DialogTitle,
  Button,
  Stack,
  DialogContent,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemIcon,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers";
import { LoadingButton } from "@mui/lab";

import EmployeeAutocomplete from "../components/EmployeeAutocomplete";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";

import _ from "lodash";
import { CHAIR_TYPES, ChairType, ChairTypes } from "../constants/chairTypes";
import { ScheduleBase } from "../schemas/schedule";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { ChairScheduleResponse } from "../types/clinic";
import PermissionGuard from "../components/PermissionGuard";
import PERMISSIONS from "../constants/permissions";

const DailySheetReportRecordModal: React.FunctionComponent<
  IScheduleModalProps
> = ({ id, date, initialValues, clinicId, chairId, callback }) => {
  const dispatch = useAppDispatch();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [uniqueRoles, setUiqueRoles] = useState<ROLES[]>([]);

  const form = useForm<ScheduleBase & { _id?: string }>({
    defaultValues: {
      _id: initialValues?._id,
      type: initialValues?.type,
      date: date,
      end: moment(date).set({ hour: 17, minute: 0 }).toDate(),
      ...initialValues,
      doctor: initialValues?.doctor?._id,
      assistant: initialValues?.assistant?._id,
      extraAssistant: initialValues?.extraAssistant?._id,
      specialist: initialValues?.specialist?._id,
      hygenist: initialValues?.hygenist?._id,
      orthodontist: initialValues?.orthodontist?._id,
      driver: initialValues?.driver?._id,
      sanitar: initialValues?.sanitar?._id,
    },

    // @ts-ignore
    // resolver: yupResolver(createDailySheetReportSchema),
  });

  const onSubmit = form.handleSubmit((values) => {
    setIsSubmitting(true);
    axios
      .put<ChairScheduleResponse>(`/clinics/chairs/${chairId}/schedule`, values)
      .then((response) => {
        form.setValue("_id", response.data._id);
        callback();
      })
      .catch((err) => {
        console.log("err:", err);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  });

  const handleDelete = useCallback(() => {
    if (!initialValues) {
      return;
    }
    setIsDeleting(true);
    dispatch(
      openModal({
        type: MODALS.Approve,
        title: "Vai tiešām vēlaties dzēst?",
        message: "Ieraksts tiks neatgriezeniski dzēsts",
        onApprove: () => {
          axios
            .delete(`/clinics/chairs/${chairId}/schedule/${initialValues._id}`)
            .then((res) => {
              callback();
              handleClose();
            });
        },
        onReject: () => {
          setIsDeleting(false);
        },
      }),
    );
  }, [initialValues]);

  const handleClose = () => {
    dispatch(closeModalById(id));
  };

  useEffect(() => {
    axios.get<ROLES[]>(`/clinics/${clinicId}/roles`).then((res) => {
      setUiqueRoles(res.data);
    });
  }, [initialValues]);

  return (
    <Dialog fullWidth={true} maxWidth="xs" open={true} onClose={handleClose}>
      <FormProvider {...form}>
        <form onSubmit={onSubmit}>
          <DialogTitle>Pievienot ierakstu</DialogTitle>
          <DialogContent style={{ paddingTop: 20 }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <Controller
                  name="date"
                  control={form.control}
                  render={({ field }) => (
                    <TimePicker
                      label="No"
                      value={moment(field.value)}
                      maxTime={moment(form.watch("end"))}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                      disabled={isSubmitting}
                    />
                  )}
                />
                <Controller
                  name="end"
                  control={form.control}
                  render={({ field }) => (
                    <TimePicker
                      label="Līdz"
                      value={moment(field.value)}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                      disabled={isSubmitting}
                    />
                  )}
                />
              </Stack>
              <Controller
                name="type"
                control={form.control}
                render={({ field, fieldState }) => (
                  <FormControl error={Boolean(fieldState.error)}>
                    <InputLabel id="type-select">Darba vietas veids</InputLabel>
                    <Select
                      labelId="type-select"
                      id="type"
                      label="Darba vietas veids"
                      {...field}
                      renderValue={(value: string) => {
                        return CHAIR_TYPES[value as ChairTypes].label;
                      }}
                    >
                      <MenuItem value=""></MenuItem>
                      {Object.entries(CHAIR_TYPES).map(([key, value]) => {
                        if (
                          !value.requiredRoles ||
                          value.requiredRoles.some((role) =>
                            uniqueRoles.includes(role),
                          )
                        ) {
                          return (
                            <MenuItem key={key} value={key}>
                              <ListItemIcon>{value.icon}</ListItemIcon>
                              {value.label}
                            </MenuItem>
                          );
                        }
                        return null;
                      })}
                    </Select>
                  </FormControl>
                )}
              />

              <ChairFields
                date={date}
                clinic={clinicId}
                disabled={isSubmitting}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            {initialValues && (
              <PermissionGuard permission={PERMISSIONS.DELETE_SCHEDULE}>
                {({ hasAccess }) => (
                  <LoadingButton
                    loading={isDeleting}
                    disabled={isSubmitting || !hasAccess}
                    variant="outlined"
                    color="error"
                    onClick={() => handleDelete()}
                  >
                    Dzēst
                  </LoadingButton>
                )}
              </PermissionGuard>
            )}

            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
              flex={1}
            >
              <Button onClick={handleClose}>Aizvērt</Button>
              <LoadingButton
                loading={isSubmitting}
                disabled={isDeleting}
                type="submit"
                variant="contained"
              >
                {initialValues ? "Saglabāt" : "Pievienot"}
              </LoadingButton>
            </Stack>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

interface IChairFieldsProps {
  date: Date;
  clinic: string;
  disabled?: boolean;
}

const ChairFields: React.FC<IChairFieldsProps> = ({
  date,
  clinic,
  disabled,
}) => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<ScheduleBase>();

  switch (watch("type")) {
    case ChairTypes.Orthodontist:
      return (
        <>
          <Controller
            control={control}
            name="orthodontist"
            render={({ field }) => (
              <EmployeeAutocomplete
                label="Ortodonts"
                roles={[ROLES.Orthodontist]}
                date={date}
                disabled={disabled}
                clinic={clinic}
                value={field.value}
                onChange={(newValue) => {
                  field.onChange(newValue);
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="assistant"
            render={({ field }) => (
              <EmployeeAutocomplete
                label="Asistents"
                roles={[ROLES.Assistant]}
                date={date}
                disabled={disabled}
                clinic={clinic}
                value={field.value}
                onChange={(newValue) => {
                  field.onChange(newValue);
                }}
              />
            )}
          />
        </>
      );
    case ChairTypes.Dentist:
      return (
        <>
          <Controller
            control={control}
            name="doctor"
            render={({ field }) => (
              <EmployeeAutocomplete
                label="Dr."
                roles={[ROLES.Doctor]}
                date={date}
                disabled={disabled}
                clinic={clinic}
                value={field.value}
                onChange={(newValue) => {
                  field.onChange(newValue);
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="assistant"
            render={({ field }) => (
              <EmployeeAutocomplete
                label="Asistents"
                roles={[ROLES.Assistant]}
                date={date}
                disabled={disabled}
                clinic={clinic}
                value={field.value}
                onChange={(newValue) => {
                  field.onChange(newValue);
                }}
              />
            )}
          />
        </>
      );
    case ChairTypes.Hygienist:
      return (
        <>
          <Controller
            control={control}
            name="hygenist"
            render={({ field }) => (
              <EmployeeAutocomplete
                label="Higiēnists"
                roles={[ROLES.Hygienist]}
                date={date}
                disabled={disabled}
                clinic={clinic}
                value={field.value}
                onChange={(newValue) => {
                  field.onChange(newValue);
                }}
              />
            )}
          />
        </>
      );
    case ChairTypes.Sanitar:
      return (
        <>
          <Controller
            control={control}
            name="sanitar"
            render={({ field }) => (
              <EmployeeAutocomplete
                label="Sanitārs"
                roles={[ROLES.Sanitar]}
                date={date}
                disabled={disabled}
                clinic={clinic}
                value={field.value}
                onChange={(newValue) => {
                  field.onChange(newValue);
                }}
              />
            )}
          />
        </>
      );
    case ChairTypes.Surgeon:
      return (
        <>
          <Controller
            control={control}
            name="doctor"
            render={({ field }) => (
              <EmployeeAutocomplete
                label="Dr."
                roles={[ROLES.Doctor]}
                date={date}
                disabled={disabled}
                clinic={clinic}
                value={field.value}
                onChange={(newValue) => {
                  field.onChange(newValue);
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="assistant"
            render={({ field }) => (
              <EmployeeAutocomplete
                label="Asistents"
                roles={[ROLES.Assistant]}
                date={date}
                disabled={disabled}
                clinic={clinic}
                value={field.value}
                onChange={(newValue) => {
                  field.onChange(newValue);
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="extraAssistant"
            render={({ field }) => (
              <EmployeeAutocomplete
                label="Papildu asistents"
                roles={[ROLES.Assistant]}
                date={date}
                disabled={disabled}
                clinic={clinic}
                value={field.value}
                onChange={(newValue) => {
                  field.onChange(newValue);
                }}
              />
            )}
          />
        </>
      );
    case ChairTypes.Bus:
      return (
        <>
          <Controller
            control={control}
            name="specialist"
            render={({ field }) => (
              <EmployeeAutocomplete
                label="Speciālists"
                roles={[ROLES.Doctor, ROLES.Hygienist]}
                date={date}
                disabled={disabled}
                clinic={clinic}
                value={field.value}
                onChange={(newValue) => {
                  field.onChange(newValue);
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="assistant"
            render={({ field }) => (
              <EmployeeAutocomplete
                label="Asistents"
                roles={[ROLES.Assistant]}
                date={date}
                disabled={disabled}
                clinic={clinic}
                value={field.value}
                onChange={(newValue) => {
                  field.onChange(newValue);
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="driver"
            render={({ field }) => (
              <EmployeeAutocomplete
                label="Šoferis"
                roles={[ROLES.Driver]}
                date={date}
                disabled={disabled}
                clinic={clinic}
                value={field.value}
                onChange={(newValue) => {
                  field.onChange(newValue);
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="destination"
            render={({ field }) => (
              <TextField
                label="Galamērķis"
                value={field.value}
                disabled={disabled}
                onChange={field.onChange}
                error={Boolean(errors.destination)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FontAwesomeIcon icon={faLocationDot} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </>
      );
    default:
      return <></>;
  }
};

export default DailySheetReportRecordModal;
